/**
 * 周学习目标设置页
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WeeklyGoal,
  CardType,
  getSettings,
  saveSettings,
  defaultDb,
  getWeeklyStudyStats,
  getWeekStart,
} from '../db/indexedDb';
import './WeeklyGoalPage.css';

const DEFAULT_WEEKLY_GOAL: WeeklyGoal = {
  id: 'weekly-goal',
  targetHoursPerWeek: 7,
  skillAllocation: {
    speaking: 40,
    writing: 30,
    listening: 20,
    vocab: 10,
  },
  targetBand: 7.0,
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

const skillLabels: Record<CardType, string> = {
  speaking: '口语',
  writing: '写作',
  listening: '听力',
  vocab: '词汇',
};

const skillColors: Record<CardType, string> = {
  speaking: 'var(--skill-speaking)',
  writing: 'var(--skill-writing)',
  listening: 'var(--skill-listening)',
  vocab: 'var(--skill-vocab)',
};

export function WeeklyGoalPage() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState<WeeklyGoal>(DEFAULT_WEEKLY_GOAL);
  const [weeklyStats, setWeeklyStats] = useState<{
    totalMinutes: number;
    bySkill: Record<CardType, number>;
    cardsCompleted: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  // 加载数据
  useEffect(() => {
    (async () => {
      // 加载现有目标
      const settings = await getSettings('app', defaultDb);
      if (settings?.weeklyGoal) {
        setGoal(settings.weeklyGoal);
      }

      // 加载本周统计
      const stats = await getWeeklyStudyStats(getWeekStart(), defaultDb);
      setWeeklyStats({
        totalMinutes: stats.totalMinutes,
        bySkill: stats.bySkill,
        cardsCompleted: stats.cardsCompleted,
      });
    })();
  }, []);

  // 保存目标
  const handleSave = async () => {
    setSaving(true);
    try {
      const settings = await getSettings('app', defaultDb);
      await saveSettings({
        ...settings,
        id: 'app',
        weeklyGoal: { ...goal, updatedAt: Date.now() },
        dailyLimits: settings?.dailyLimits ?? {},
        createdAt: settings?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
      }, defaultDb);
      navigate('/today');
    } catch (e) {
      alert('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 更新技能分配
  const updateSkillAllocation = (skill: CardType, value: number) => {
    const current = goal.skillAllocation ?? {};
    const newAllocation = { ...current, [skill]: value };

    // 计算总和并调整
    const total = Object.values(newAllocation).reduce((sum, v) => sum + (v ?? 0), 0);
    if (total > 100) {
      // 简单地限制为100
      newAllocation[skill] = value - (total - 100);
    }

    setGoal({ ...goal, skillAllocation: newAllocation });
  };

  // 计算本周进度
  const actualHours = weeklyStats ? Math.round(weeklyStats.totalMinutes / 6) / 10 : 0; // 保留一位小数
  const progressPercent = goal.targetHoursPerWeek > 0
    ? Math.min(100, Math.round((actualHours / goal.targetHoursPerWeek) * 100))
    : 0;

  // 获取本周日期范围
  const weekStart = new Date(getWeekStart());
  const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
  const formatDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;

  return (
    <div className="weekly-goal-page">
      <header className="weekly-goal-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>WEEKLY GOAL</h1>
      </header>

      <main className="weekly-goal-content">
        {/* 本周进度 */}
        <section className="goal-section">
          <h2>本周进度</h2>
          <div className="week-range">{formatDate(weekStart)} - {formatDate(weekEnd)}</div>

          <div className="weekly-progress-card">
            <div className="progress-header">
              <span className="progress-value">{actualHours}h</span>
              <span className="progress-target">/ {goal.targetHoursPerWeek}h</span>
            </div>
            <div className="weekly-progress-bar">
              <div
                className="weekly-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="progress-footer">
              <span>完成 {progressPercent}%</span>
              <span>{weeklyStats?.cardsCompleted ?? 0} 张卡片</span>
            </div>
          </div>

          {/* 技能时间分布 */}
          {weeklyStats && weeklyStats.totalMinutes > 0 && (
            <div className="skill-time-grid">
              {(['speaking', 'writing', 'listening', 'vocab'] as CardType[]).map((skill) => (
                <div key={skill} className="skill-time-item">
                  <div className="skill-time-label">
                    <span className={`skill-dot ${skill}`} />
                    <span>{skillLabels[skill]}</span>
                  </div>
                  <div className="skill-time-value">
                    {weeklyStats.bySkill[skill] ?? 0}分钟
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 目标设置 */}
        <section className="goal-section">
          <h2>目标设置</h2>

          {/* 每周学习时长 */}
          <div className="setting-card">
            <div className="setting-label">每周学习时长</div>
            <div className="number-stepper">
              <button
                className="stepper-btn"
                onClick={() => setGoal({ ...goal, targetHoursPerWeek: Math.max(1, goal.targetHoursPerWeek - 1) })}
              >
                -
              </button>
              <span className="stepper-value">{goal.targetHoursPerWeek} 小时</span>
              <button
                className="stepper-btn"
                onClick={() => setGoal({ ...goal, targetHoursPerWeek: Math.min(40, goal.targetHoursPerWeek + 1) })}
              >
                +
              </button>
            </div>
            <div className="setting-hint">
              ≈ 每天 {Math.round(goal.targetHoursPerWeek / 7 * 60)} 分钟
            </div>
          </div>

          {/* 技能分配 */}
          <div className="setting-card">
            <div className="setting-label">技能分配</div>
            <div className="skill-allocation">
              {(['speaking', 'writing', 'listening', 'vocab'] as CardType[]).map((skill) => {
                const value = goal.skillAllocation?.[skill] ?? 25;
                return (
                  <div key={skill} className="allocation-row">
                    <div className="allocation-label">
                      <span className={`skill-dot ${skill}`} />
                      <span>{skillLabels[skill]}</span>
                    </div>
                    <div className="allocation-bar-container">
                      <div
                        className="allocation-bar"
                        style={{
                          width: `${value}%`,
                          background: skillColors[skill],
                        }}
                      />
                    </div>
                    <div className="allocation-control">
                      <button
                        className="mini-btn"
                        onClick={() => updateSkillAllocation(skill, Math.max(0, value - 10))}
                      >
                        -
                      </button>
                      <span className="allocation-value">{value}%</span>
                      <button
                        className="mini-btn"
                        onClick={() => updateSkillAllocation(skill, Math.min(100, value + 10))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 目标分数 */}
          <div className="setting-card">
            <div className="setting-label">目标分数</div>
            <div className="number-stepper">
              <button
                className="stepper-btn"
                onClick={() => setGoal({ ...goal, targetBand: Math.max(5, (goal.targetBand ?? 7) - 0.5) })}
              >
                -
              </button>
              <span className="stepper-value">{goal.targetBand ?? 7.0}</span>
              <button
                className="stepper-btn"
                onClick={() => setGoal({ ...goal, targetBand: Math.min(9, (goal.targetBand ?? 7) + 0.5) })}
              >
                +
              </button>
            </div>
          </div>
        </section>

        {/* 保存按钮 */}
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? '保存中...' : '保存目标'}
        </button>
      </main>
    </div>
  );
}

export default WeeklyGoalPage;
